import { useEffect, useId, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { type ColumnDef, DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  type EditOnFarmImplantFreshEmbryosRequest,
  type OnFarmImplantFreshEmbryoEdit,
  type OnFarmImplantFreshEmbryoEditResponse,
  OnFarmImplantRequestError,
  useEditOnFarmImplantFreshEmbryos,
} from '@/services/on-farm-implant';


const STAGES = [4, 5, 6, 7, 8, 9];
const GRADES = [1, 2, 3];

type EditFreshEmbryosDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  sessionEmbryoIds: string[];
  onSaved: (updatedCount: number, nextStrawNumber?: number) => void;
};

const renderChange = (from: number | null, to: number | null) =>
  from === to ? (
    (from ?? '—')
  ) : (
    <span className="font-medium text-blue-700">
      {from ?? '—'} → {to ?? '—'}
    </span>
  );

export function EditFreshEmbryosDialog({
  open,
  onOpenChange,
  sessionId,
  sessionEmbryoIds,
  onSaved,
}: EditFreshEmbryosDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const [strawNumber, setStrawNumber] = useState('');
  const [stage, setStage] = useState('');
  const [grade, setGrade] = useState('');
  const [preview, setPreview] =
    useState<OnFarmImplantFreshEmbryoEditResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: editEmbryos, isPending } =
    useEditOnFarmImplantFreshEmbryos();

  useEffect(() => {
    if (open) {
      setStrawNumber('');
      setStage('');
      setGrade('');
      setPreview(null);
      setError(null);
    }
  }, [open]);

  const hasChanges = Boolean(strawNumber || stage || grade);
  const hasConflicts = (preview?.conflictingStrawNumbers.length ?? 0) > 0;
  const lastStrawNumber = Number(strawNumber) + sessionEmbryoIds.length - 1;

  const toErrorMessage = (editError: unknown, previewOnly: boolean) => {
    const action = previewOnly ? 'previewFailed' : 'saveFailed';
    if (!(editError instanceof OnFarmImplantRequestError))
      return t(`onFarmImplant.fresh.edit.${action}`);
    if (editError.problems.length) return editError.problems.join(' ');
    if (editError.status === 403)
      return t('onFarmImplant.fresh.edit.forbidden');
    return t(`onFarmImplant.fresh.edit.${action}Status`, {
      status: editError.status,
    });
  };

  const buildInput = (
    previewOnly: boolean,
  ): EditOnFarmImplantFreshEmbryosRequest => ({
    sessionEmbryoIds,
    startingStrawNumber: strawNumber ? Number(strawNumber) : null,
    stage: stage ? Number(stage) : null,
    grade: grade ? Number(grade) : null,
    preview: previewOnly,
  });

  const resetPreview = () => {
    setPreview(null);
    setError(null);
  };

  const submit = async (previewOnly: boolean) => {
    if (!hasChanges) {
      setError(t('onFarmImplant.fresh.edit.nothingToChange'));
      return;
    }
    try {
      const response = await editEmbryos({
        sessionId,
        input: buildInput(previewOnly),
      });
      if (previewOnly) {
        setPreview(response);
        setError(null);
        return;
      }
      onSaved(
        response.changes.length,
        strawNumber ? lastStrawNumber + 1 : undefined,
      );
      onOpenChange(false);
    } catch (editError) {
      setError(toErrorMessage(editError, previewOnly));
    }
  };

  const columns: ColumnDef<OnFarmImplantFreshEmbryoEdit>[] = [
    {
      key: 'straw',
      header: t('onFarmImplant.fresh.straw'),
      width: 130,
      render: (change) =>
        renderChange(change.oldStrawNumber, change.newStrawNumber),
    },
    {
      key: 'stage',
      header: t('onFarmImplant.fresh.stage'),
      width: 90,
      render: (change) => renderChange(change.oldStage, change.newStage),
    },
    {
      key: 'grade',
      header: t('onFarmImplant.fresh.gradeLabel'),
      width: 90,
      render: (change) => renderChange(change.oldGrade, change.newGrade),
    },
    {
      key: 'queueState',
      header: t('onFarmImplant.fresh.status'),
      width: 90,
      render: (change) => change.queueState,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.fresh.edit.title', {
              count: sessionEmbryoIds.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.fresh.edit.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={(event) => {
            event.preventDefault();
            void submit(true);
          }}
          className="flex flex-col gap-4"
        >
          <div className="grid grid-cols-3 gap-3">
            <Field>
              <FieldLabel htmlFor={`${formId}-straw`}>
                {t('onFarmImplant.fresh.edit.startingStraw')}
              </FieldLabel>
              <Input
                id={`${formId}-straw`}
                type="number"
                min={1}
                value={strawNumber}
                placeholder={t('onFarmImplant.fresh.edit.noChange')}
                onChange={(event) => {
                  setStrawNumber(event.target.value);
                  resetPreview();
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${formId}-stage`}>
                {t('onFarmImplant.fresh.stage')}
              </FieldLabel>
              <select
                id={`${formId}-stage`}
                className="border-input bg-background h-8 w-full rounded-md border px-2 text-sm"
                value={stage}
                onChange={(event) => {
                  setStage(event.target.value);
                  resetPreview();
                }}
              >
                <option value="">
                  {t('onFarmImplant.fresh.edit.noChange')}
                </option>
                {STAGES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${formId}-grade`}>
                {t('onFarmImplant.fresh.gradeLabel')}
              </FieldLabel>
              <select
                id={`${formId}-grade`}
                className="border-input bg-background h-8 w-full rounded-md border px-2 text-sm"
                value={grade}
                onChange={(event) => {
                  setGrade(event.target.value);
                  resetPreview();
                }}
              >
                <option value="">
                  {t('onFarmImplant.fresh.edit.noChange')}
                </option>
                {GRADES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {strawNumber && (
            <p className="text-muted-foreground text-xs">
              {t('onFarmImplant.fresh.edit.strawHint')}
            </p>
          )}

          {preview && (
            <div className="flex flex-col gap-1">
              <h3 className="text-xs font-semibold text-gray-600">
                {t('onFarmImplant.fresh.edit.affected', {
                  count: preview.changes.length,
                })}
              </h3>
              <DataTable
                rows={preview.changes}
                columns={columns}
                getRowId={(change) => change.sessionEmbryoId}
                storageKey="on-farm-implant-fresh-edit-preview"
                headerClassName="font-['Poppins',sans-serif] font-medium tracking-normal normal-case"
                fillWidth
              />
            </div>
          )}

          {hasConflicts && (
            <p role="alert" className="text-destructive text-xs">
              {t('onFarmImplant.fresh.edit.conflicts', {
                range:
                  sessionEmbryoIds.length > 1
                    ? `${strawNumber}–${lastStrawNumber}`
                    : strawNumber,
                straws: preview!.conflictingStrawNumbers.join(', '),
              })}
            </p>
          )}
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('onFarmImplant.fresh.edit.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="outline"
            disabled={isPending || !hasChanges}
          >
            {t('onFarmImplant.fresh.edit.preview')}
          </Button>
          <Button
            type="button"
            disabled={isPending || !preview || hasConflicts}
            onClick={() => void submit(false)}
          >
            {t('onFarmImplant.fresh.edit.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
